import React from 'react';
import { Select, DatePicker } from 'antd';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Button } from '@frontend/shadcn/components/ui/button';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { ORDER_VALUE_OPERATOR, PRODUCT_CONDITION_TYPE, PayoutRuleOperator } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import moment from 'moment';
import { cn } from '@frontend/shadcn/lib/utils';
import { hasIncompleteConditions } from '../PayoutRulesDrawer';
import { ConditionType, OPERATOR_DISPLAY, PayoutAction, PayoutRuleConditionsInput } from './types';
import { PayoutProductConditions } from './PayoutConditions/PayoutProductConditions';
import { PayoutCollectionConditions } from './PayoutConditions/PayoutCollectionConditions';
import { MultipleStoreAlert } from './PayoutConditions/MultipleStoreAlert';
import { ClientForSync } from '../../OfferForm';

const { RangePicker } = DatePicker;

const getAvailableConditionTypes = (isShopifyConnected: boolean) => {
  if (isShopifyConnected) {
    return Object.values(ConditionType);
  }
  return [
    ConditionType.PRODUCT,
    ConditionType.COLLECTIONS,
    ConditionType.ORDER_SUBTOTAL,
    ConditionType.DATE_RANGE
  ];
};

interface PayoutConditionsProps {
  clientId: string;
  conditions: PayoutRuleConditionsInput[];
  onConditionsChange: (conditions: PayoutRuleConditionsInput[]) => void;
  showErrors?: boolean;
  isShopifyConnected: boolean;
  clientsForSync: ClientForSync[];
}

export const PayoutConditions: React.FC<PayoutConditionsProps> = ({
  clientId,
  conditions,
  onConditionsChange,
  showErrors = false,
  isShopifyConnected,
  clientsForSync,
}) => {
  // Add state for alert visibility
  const [showMultipleStoreAlert, setShowMultipleStoreAlert] = React.useState(false);

  // Check for multiple product or collection conditions
  const hasMultipleConditions = React.useMemo(() => {
    const productConditions = conditions.filter((c) => c.type === ConditionType.PRODUCT);
    const collectionConditions = conditions.filter((c) => c.type === ConditionType.COLLECTIONS);
    return productConditions.length > 1 || collectionConditions.length > 1;
  }, [conditions]);

  // Show alert when multiple conditions are detected and Shopify is connected
  React.useEffect(() => {
    if (isShopifyConnected && hasMultipleConditions) {
      setShowMultipleStoreAlert(true);
    } else {
      setShowMultipleStoreAlert(false);
    }
  }, [isShopifyConnected, hasMultipleConditions]);

  const handleAddCondition = () => {
    const newConditions = [...conditions, {
      type: null,
      action: PayoutAction.ADD,
      id: null,
      operator: PayoutRuleOperator.AND,
      selectedStoreId: null
    }];
    onConditionsChange(newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length === 1) return;
    const newConditions = conditions.filter((_, i) => i !== index);
    onConditionsChange(newConditions);
  };

  const handleConditionChange = (index: number, updates: Partial<PayoutRuleConditionsInput>) => {
    const newConditions = [...conditions];

    // If the type is being changed to RECURRING_ORDER, set the default recurring limit values
    if (updates.type === ConditionType.RECURRING_ORDER) {
      updates = {
        ...updates,
        recurringLimit: {
          isLimitPayoutToFirstOrder: false,
          isPayoutAppliedToAllRecurringOrders: true,
          limitPayoutToMultipleRecurringOrder: null
        }
      };
    }

    newConditions[index] = {
      ...newConditions[index],
      ...updates,
      action: PayoutAction.ADD,
      id: null,
      operator: PayoutRuleOperator.AND
    };
    onConditionsChange(newConditions);
  };

  const getUsedProductStores = () => {
    return conditions
      .filter((c) => c.type === ConditionType.PRODUCT)
      .map((c) => c.selectedStoreId)
      .filter(Boolean) as string[];
  };

  const getUsedCollectionStores = () => {
    return conditions
      .filter((c) => c.type === ConditionType.COLLECTIONS)
      .map((c) => c.selectedStoreId)
      .filter(Boolean) as string[];
  };

  /**
   * Returns an array of condition types that are currently in use, excluding product conditions.
   * This is used to prevent duplicate non-product conditions since they can only be used once.
   * @returns {Array<ConditionType | null>} Array of used condition types
   */
  const getUsedConditionTypes = () => {
    const usedTypes = conditions.map((c) => c.type);
    return usedTypes.filter((type) => type !== ConditionType.PRODUCT);
  };

  /**
   * Checks if all available store slots for product conditions have been used.
   * This prevents adding more product conditions than available stores.
   * @param storeOptions - Array of available store options
   * @returns {boolean} True if all store slots are used
   */
  const hasExhaustedProductStores = (storeOptions: ClientForSync[]) => {
    const usedStores = getUsedProductStores();
    return usedStores?.length > (storeOptions?.length || 0);
  };

  /**
   * Checks if all available store slots for collection conditions have been used.
   * This prevents adding more collection conditions than available stores.
   * @param storeOptions - Array of available store options
   * @returns {boolean} True if all store slots are used
   */
  const hasExhaustedCollectionStores = (storeOptions: ClientForSync[]) => {
    const usedCollectionStores = getUsedCollectionStores();
    return usedCollectionStores?.length > (storeOptions?.length || 0);
  };

  /**
   * Determines if more conditions can be added to the payout rule.
   * Conditions can be added if:
   * 1. Product store slots are available
   * 2. Collection store slots are available
   * 3. Unused non-store condition types exist
   * @returns {boolean} True if more conditions can be added
   */
  const canAddMoreConditions = () => {
    const availableTypes = getAvailableConditionTypes(isShopifyConnected);

    const hasExhaustedProducts = hasExhaustedProductStores(clientsForSync);
    const hasExhaustedCollections = hasExhaustedCollectionStores(clientsForSync);

    // Get non-store condition types (conditions that don't require store selection)
    const nonStoreTypes = availableTypes.filter((type) =>
      type !== ConditionType.PRODUCT &&
      type !== ConditionType.COLLECTIONS
    );

    const hasUnusedNonStoreTypes = nonStoreTypes.some((type) => {
      const typeCount = conditions.filter((c) => c.type === type).length;
      // Non-store types can only be used once
      return typeCount === 0;
    });

    return (
      !hasExhaustedProducts ||
      !hasExhaustedCollections ||
      hasUnusedNonStoreTypes
    );
  };

  const renderConditionInputs = (condition: PayoutRuleConditionsInput, index: number) => {
    const isIncomplete = showErrors && hasIncompleteConditions(condition);

    switch (condition.type) {
      case ConditionType.ORDER_SUBTOTAL: {
        return (
          <div className="flex-1 flex gap-2 w-full">
            <div className="w-[30%]">
              <Select
                size="middle"
                value={condition.orderSubtotal?.operator}
                onChange={(value) => handleConditionChange(index, {
                  orderSubtotal: {
                    ...condition.orderSubtotal,
                    operator: value as ORDER_VALUE_OPERATOR
                  }
                })}
                placeholder="Operator"
                style={{ width: '100%' }}
                status={isIncomplete ? 'error' : ''}
              >
                {Object.entries(OPERATOR_DISPLAY).map(([value, display]) => (
                  <Select.Option key={value} value={value}>{display}</Select.Option>
                ))}
              </Select>
            </div>
            <div className="w-[70%] relative">
              <span className="absolute left-3 top-[50%] transform -translate-y-[50%] text-grey-4">$</span>
              <Input
                type="number"
                className={cn(
                  'pl-8 w-full',
                  isIncomplete && 'border-red-500 focus:border-red-500 focus-visible:ring-red-500'
                )}
                placeholder="Enter Value"
                value={condition.orderSubtotal?.value || ''}
                onChange={(e) => handleConditionChange(index, {
                  orderSubtotal: {
                    ...condition.orderSubtotal,
                    value: parseInt(e.target.value, 10)
                  }
                })}
              />
            </div>
          </div>
        );
      }

      case ConditionType.DATE_RANGE: {
        // Create moment objects that preserve the date regardless of timezone
        const startDate = condition.duration?.startDate
          ? moment.utc(condition.duration.startDate).local()
          : moment();
        const endDate = condition.duration?.endDate
          ? moment.utc(condition.duration.endDate).local()
          : null;

        // Function to disable dates before today
        const disabledDate = (current: moment.Moment) => {
          return current && current < moment().startOf('day');
        };

        return (
          <div className="flex-1">
            <RangePicker
              size="middle"
              value={[startDate, endDate]}
              onChange={(dates) => {
                const formatDateToLocal = (date: moment.Moment | null) => {
                  if (!date) return null;
                  // Use UTC to avoid timezone offset issues when serializing
                  const year = date.year();
                  const month = date.month(); // 0-indexed
                  const day = date.date();
                  return new Date(Date.UTC(year, month, day));
                };

                handleConditionChange(index, {
                  duration: {
                    startDate: formatDateToLocal(dates?.[0]),
                    endDate: formatDateToLocal(dates?.[1])
                  }
                });
              }}
              disabledDate={disabledDate}
              style={{ width: '100%' }}
              status={isIncomplete ? 'error' : ''}
            />
          </div>
        );
      }

      case ConditionType.RECURRING_ORDER: {
        return (
          <div className="flex-1">
            <p className="text-sm text-gray-500 flex items-center gap-x-1 justify-center m-0">
              Commission will be applied to <b>all recurring orders</b>
            </p>
          </div>
        );
      }

      case ConditionType.COLLECTIONS: {
        const usedCollectionStores = getUsedCollectionStores();
        return (
          <div className="flex-1">
            <PayoutCollectionConditions
              value={condition.collections}
              onChange={(collections) => handleConditionChange(index, { collections })}
              status={isIncomplete ? 'error' : ''}
              clientsForSync={clientsForSync}
              usedStores={usedCollectionStores}
              selectedStoreId={condition.selectedStoreId}
              onStoreSelect={(storeLabel) => {
                handleConditionChange(index, {
                  selectedStoreId: storeLabel,
                  collections: []
                });
              }}
              isShopifyConnected={isShopifyConnected}
            />
          </div>
        );
      }

      case ConditionType.PRODUCT: {
        const usedProductStores = getUsedProductStores();
        return (
          <div className="flex-1">
            <PayoutProductConditions
              clientId={clientId}
              isShopifyConnected={isShopifyConnected}
              value={condition.products}
              onChange={(products) => {
                const formattedProducts = products.map((product) => ({
                  id: product.id,
                  name: product.name || product.id,
                  type: PRODUCT_CONDITION_TYPE.PRODUCT_ID,
                  imageUrl: product.imageUrl,
                  advertiserId: product.advertiserId,
                  operator: product.operator
                }));
                handleConditionChange(index, { products: formattedProducts });
              }}
              status={isIncomplete ? 'error' : ''}
              clientsForSync={clientsForSync}
              usedStores={usedProductStores}
              selectedStoreId={condition.selectedStoreId}
              onStoreSelect={(storeLabel) => {
                handleConditionChange(index, {
                  selectedStoreId: storeLabel,
                  products: []
                });
              }}
            />
          </div>
        );
      }

      default: {
        return (
          <div className="flex-1">
            <Input
              disabled
              placeholder={`Select ${condition.type}`}
              className={cn(
                'w-full',
                isIncomplete && 'border-red-500 focus:border-red-500 focus-visible:ring-red-500'
              )}
            />
          </div>
        );
      }
    }
  };

  return (
    <div className="pt-2 space-y-2">
      {showMultipleStoreAlert && (
        <MultipleStoreAlert onClose={() => setShowMultipleStoreAlert(false)} />
      )}
      {conditions.map((condition, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="w-[30%]">
            <Select
              size="middle"
              value={condition.type}
              onChange={(value) => handleConditionChange(index, {
                type: value as ConditionType,
              })}
              placeholder="Select Condition"
              style={{ width: '100%' }}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                </div>
              )}
            >
              {getAvailableConditionTypes(isShopifyConnected).map((type) => {
                let isDisabled = false;

                if (type === ConditionType.PRODUCT) {
                  isDisabled = hasExhaustedProductStores(clientsForSync);
                } else if (type === ConditionType.COLLECTIONS) {
                  isDisabled = hasExhaustedCollectionStores(clientsForSync);
                } else {
                  isDisabled = getUsedConditionTypes().includes(type) && type !== condition.type;
                }

                return (
                  <Select.Option
                    key={type}
                    value={type}
                    disabled={isDisabled}
                    className={isDisabled ? 'text-grey-3 bg-grey-1' : ''}
                  >
                    {type}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div className="w-[70%]">
            {!condition.type ? (
              <Select
                size="middle"
                disabled
                placeholder="Select value"
                style={{ width: '100%' }}
              />
            ) : (
              renderConditionInputs(condition, index)
            )}
          </div>

          {/* Only show cross button if this isn't the last remaining row */}
          {conditions.length > 1 && (
            <Button
              variant="ghost"
              size="smallIcon"
              type="button"
              onClick={() => handleRemoveCondition(index)}
              className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5"
            >
              <XmarkIcon />
            </Button>
          )}
        </div>
      ))}

      {canAddMoreConditions() && (
        <Button
          variant="ghost"
          onClick={handleAddCondition}
          className="justify-start px-0 hover:underline hover:bg-transparent"
        >
          + Add
        </Button>
      )}
    </div>
  );
};
